import { useEffect } from "react";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { pushAnalyticsEvent } from "@ottatech/analytics";

import { Slider } from "components/Slider";
import {
  NotificationFrequency,
  UpdateUserNotificationPreferencesDocument,
} from "schema";

const frequencies = [
  { position: 0, label: "Never", value: NotificationFrequency.Never },
  { position: 50, label: "Weekly", value: NotificationFrequency.Weekly },
  { position: 100, label: "Daily", value: NotificationFrequency.Daily },
];

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  max-width: 270px;
  margin: 0 auto;
  padding: 12px 0 24px;
`;

export function EmailNotificationPreferences({
  user,
}: {
  user:
    | { jobEmailNotificationsFrequency: NotificationFrequency }
    | null
    | undefined;
}): React.ReactElement {
  const currentFrequency = frequencies.find(
    ({ value }) => value === (user ? user.jobEmailNotificationsFrequency : null)
  );
  const currentPosition = currentFrequency && currentFrequency.position;

  const [mutation] = useMutation(UpdateUserNotificationPreferencesDocument);

  const [qs] = useSearchParams();
  const notificationFrequency = qs.get("newUserEmailNotificationFrequency");

  useEffect(() => {
    if (typeof notificationFrequency === "string") {
      mutation({
        variables: {
          frequency: notificationFrequency as NotificationFrequency,
        },
      });
      pushAnalyticsEvent({
        eventName: "Candidate changed email frequency",
        source: "Email",
        from: currentFrequency?.value,
        to: notificationFrequency,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper>
      <Slider
        marks={frequencies.reduce<Record<number, string>>((obj, item) => {
          obj[item.position] = item.label;
          return obj;
        }, {})}
        included={false}
        step={null}
        value={currentPosition}
        onChange={newPosition => {
          const frequency = frequencies.find(
            ({ position }) => position === newPosition
          );
          if (frequency) {
            mutation({
              variables: {
                frequency: frequency.value,
              },
            });
            pushAnalyticsEvent({
              eventName: "Candidate changed email frequency",
              source: "Web App",
              from: currentFrequency?.value,
              to: frequency.value,
            });
          }
        }}
      />
    </Wrapper>
  );
}
